/**
 * What a share artefact says when somebody posts it (M17, PRD §1.8, FR-1.8).
 *
 * Every line here is derived from something the server already stated — the
 * band sentence, the receipt's own counts, the door's public totals — and
 * nothing is invented, rounded up, or decorated. The register is the register of
 * the rest of the product: dry, proud, never boastful, no exclamation marks and
 * no emoji. A person should be able to post one of these without cringing.
 *
 * Pure functions with no imports past formatting, so every line is assertable.
 */
import { groupThousands } from "../inbox/labels";
import type { DoorStats, Receipt } from "../../lib/api";

/** `["a", "b", "c"]` → `"a, b and c"`. The Oxford comma is not Nob's style. */
function joined(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

function plural(n: number, unit: string): string {
  return `${groupThousands(n)} ${unit}${n === 1 ? "" : "s"}`;
}

/** A trailing full stop, exactly one, whatever the source line ended with. */
function stopped(line: string): string {
  const trimmed = line.trim().replace(/[.\s]+$/, "");
  return trimmed ? `${trimmed}.` : "";
}

/**
 * Loop 1 — the standing flex. The server's own band sentence, with "this door"
 * resolved to the door it was actually at: `"Top 4% of knocks at this door this
 * week."` becomes `"Top 4% of knocks at @maya's door this week."` Nothing is
 * added — if the sentence never names the door, the handle is stated after it
 * rather than spliced into a line we did not write.
 */
export function standingPostText(bandLabel: string, doorHandle: string): string {
  const label = stopped(bandLabel);
  const handle = doorHandle.trim().replace(/^@/, "");
  if (!label) return "";
  if (!handle) return label;
  const possessive = `at @${handle}’s door`;
  if (/\bat this door\b/i.test(label)) return label.replace(/\bat this door\b/i, possessive);
  return `${label.slice(0, -1)} — ${possessive}.`;
}

/**
 * Loop 3 — the receipt. Only the numbers the receipt itself carries: pages
 * read, claims verified, sources checked.
 *
 * **NO PRICE, AND THERE IS NO LONGER ONE TO LEAVE OUT.** This post used to have
 * to decide what to do about the figure a hearing was charged — "for $0.00" on
 * a hearing the plan covered read as a boast about nothing, or as a bug. Ruling
 * R-0916-1 deleted the figure. The work is what was worth posting either way:
 * it is the same work whatever paid for it (economics-wire §5.6).
 */
export function receiptPostText(receipt: Receipt): string {
  const earned = receipt.receipts_earned;
  const parts: string[] = [];
  if (earned.pages_reviewed > 0) parts.push(`read ${plural(earned.pages_reviewed, "page")}`);
  if (earned.claims_verified > 0) {
    parts.push(`verified ${plural(earned.claims_verified, "claim")}`);
  }
  if (earned.sources_checked > 0) parts.push(`checked ${plural(earned.sources_checked, "source")}`);
  // One sentence for every lane (R-0825-3 extended, 2026-08-25): the work is
  // what is worth posting, and a dollar figure in a share post was the last
  // place the sender's accounting still leaked.
  return parts.length === 0
    ? "A doorman heard me out at this door."
    : `It ${joined(parts)} before it decided.`;
}

/** Loop 2, on screen — `"1,247 knocks · 12 got a reply"`. The dot is typography. */
export function doorStatsLine(stats: DoorStats): string {
  return `${plural(stats.knocks_total, "knock")} · ${groupThousands(stats.replies)} got a reply`;
}

/** Loop 2, posted. The same two numbers, said as a sentence. */
export function doorStatsPostText(stats: DoorStats): string {
  return `${plural(stats.knocks_total, "knock")} screened at my door. ${groupThousands(
    stats.replies,
  )} got a reply.`;
}

/** Loop 4 — the House Rules card. The PRD's own line (§1.8), stated flat. */
export const RULES_POST_TEXT = "Here’s what my doorman is instructed to do.";

/** What is actually published by loop 4, said plainly before anyone posts it.
 * Since ruling R-0910-10 that is the door's public line and nothing else: the
 * House Rules are private without exception, so there is no longer a section of
 * them on any card. */
export const RULES_SHARE_NOTE =
  "Only your door's public line is on the card. Your House Rules stay private.";

/**
 * The X post-composer. `text` and `url` are the two parameters the intent takes;
 * everything else about the post is the poster's own business.
 */
export function postOnXUrl(text: string, url: string): string {
  const params = new URLSearchParams();
  if (text) params.set("text", text);
  params.set("url", url);
  return `https://x.com/intent/post?${params.toString()}`;
}
